import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "antd";
import { AlertTriangle, ChevronLeftIcon, Info, Plus, Save, ShieldAlert, ShieldCheck, Trash2, X } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_TDS_SLAB } from "../../constants/api";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * TDS Configuration — the tax rate grid, per financial year.
 *
 * Rates are DATA, never code: they change every budget, and re-running an old
 * payroll has to reproduce the figures that were printed at the time. Nothing
 * here is seeded with real rates — the numbers must come from your CA.
 *
 * The new regime (115BAC) is the statutory default and needs no investment
 * declaration to compute, which is why it is the tab shown first. The old
 * regime additionally varies by age, so it gets the age selector.
 */

const REGIMES = [
    { key: "new", label: "New regime (115BAC)", hint: "Statutory default. No HRA / 80C / 80D — standard deduction only." },
    { key: "old", label: "Old regime", hint: "Opt-in. Allows HRA, Chapter VI-A and 24(b) from verified declarations." },
];

const AGE_CATEGORIES = [
    { value: "all", label: "All ages" },
    { value: "below_60", label: "Below 60" },
    { value: "senior", label: "Senior (60-79)" },
    { value: "super_senior", label: "Super senior (80+)" },
];

const REGIME_OPTIONS = REGIMES.map((r) => ({ value: r.key, label: r.label }));

// Shared antd control styling, matching the configuration screens.
const CONTROL_CLASS = cn(
    "w-full rounded-lg transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
);

const INPUT_CLASS = cn(
    "rounded-md transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
);

const emptyConfig = (regime) => ({
    regime,
    standard_deduction: "",
    rebate_87a_income_limit: "",
    rebate_87a_max_amount: "",
    cess_rate: "",
    surcharge_bands: [],
    surcharge_max_rate: "",
    marginal_relief_enabled: true,
    professional_tax_annual: "",
});

// Build the FY options around the current one: April starts a new year.
const financialYearOptions = () => {
    const now = new Date();
    const startYear = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return [startYear + 1, startYear, startYear - 1, startYear - 2].map((y) => ({
        value: `${y}-${y + 1}`, label: `${y}-${y + 1}`,
    }));
};

const TDSConfiguration = () => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();
    // Its own key, deliberately separate from TDS Preview: this screen sets the
    // statutory rates for the whole company, so far fewer people should have it.
    const { permission: viewPermission, edit: editPermission } =
        checkUserPermissionFunc("payroll", "TDS Configuration", "SubMenu");

    const fyOptions = useMemo(financialYearOptions, []);
    const [financialYear, setFinancialYear] = useState(fyOptions[1]?.value || fyOptions[0]?.value);
    const [regime, setRegime] = useState("new");
    const [ageCategory, setAgeCategory] = useState("all");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tdsEnabled, setTdsEnabled] = useState(false);
    const [defaultRegime, setDefaultRegime] = useState("new");
    const [slabs, setSlabs] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [serverValidation, setServerValidation] = useState([]);

    const load = useCallback(async () => {
        if (!viewPermission) { setLoading(false); return; }
        const res = await fetch(PAYROLL_TDS_SLAB, { financial_year: financialYear }, setLoading);
        if (res?.status === "success") {
            setTdsEnabled(!!res.data.tds_enabled);
            setDefaultRegime(res.data.default_regime || "new");
            setSlabs(res.data.slabs || []);
            setConfigs(res.data.configs || []);
            setServerValidation(res.data.validation || []);
        } else {
            ToastMsg("error", res?.message || "Could not load TDS configuration");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financialYear]);

    useEffect(() => { load(); }, [load]);

    // The old regime is the only one with age-specific grids.
    useEffect(() => { if (regime === "new") setAgeCategory("all"); }, [regime]);

    // ---- current grid + config slice ----
    // Row ORDER is the source of truth, not income_from — see relink(). The API
    // returns rows already ordered (TDSSlab.Meta.ordering), so no client sort:
    // sorting by income_from would reshuffle rows while a value is mid-edit.
    const visibleSlabs = useMemo(
        () => slabs.filter((s) => s.regime === regime && s.age_category === ageCategory),
        [slabs, regime, ageCategory]
    );

    /**
     * income_from is DERIVED, never typed: a band starts exactly where the
     * previous one ended, and the first starts at 0. The last band is always
     * open-ended.
     *
     * This makes a gap or overlap structurally impossible instead of merely
     * validated — and it fixes the trap in the first version, where the top
     * band's upper limit had no input, so there was no way to close it off and
     * add the band above it.
     */
    const relink = (rows) => rows.map((row, index) => ({
        ...row,
        income_from: index === 0 ? 0 : (rows[index - 1].income_to ?? ""),
        income_to: index === rows.length - 1 ? null : row.income_to,
    }));

    // Replace just this (regime, age) grid, keeping the other grids untouched.
    const updateVisible = (updater) => setSlabs((prev) => {
        const mine = prev.filter((s) => s.regime === regime && s.age_category === ageCategory);
        const others = prev.filter((s) => !(s.regime === regime && s.age_category === ageCategory));
        return [...others, ...relink(updater(mine))];
    });

    const currentConfig = useMemo(
        () => configs.find((c) => c.regime === regime) || emptyConfig(regime),
        [configs, regime]
    );

    // ---- client-side grid validation, mirroring tools/tds.validate_slabs ----
    // A gap does not throw at calculation time, it silently taxes the wrong
    // amount, so it is surfaced here before the save is even attempted.
    // Gaps and overlaps cannot occur any more — relink() derives every lower
    // bound from the band below it. What is left to check is blanks and
    // non-increasing values.
    const gridProblems = useMemo(() => {
        const problems = [];
        if (!visibleSlabs.length) return ["No slabs configured for this regime yet"];

        visibleSlabs.forEach((slab, index) => {
            const isLast = index === visibleSlabs.length - 1;
            const rowLabel = `Band ${index + 1}`;
            const rate = Number(slab.rate);
            if (slab.rate === "" || slab.rate === null || Number.isNaN(rate) || rate < 0 || rate > 100) {
                problems.push(`${rowLabel}: enter a rate between 0 and 100`);
            }
            if (isLast) return;   // open-ended by construction

            if (slab.income_to === null || slab.income_to === "" || slab.income_to === undefined) {
                problems.push(`${rowLabel}: enter the upper limit (the next band starts there)`);
                return;
            }
            if (Number(slab.income_to) <= Number(slab.income_from || 0)) {
                problems.push(`${rowLabel}: upper limit must be greater than ${Number(slab.income_from || 0).toLocaleString("en-IN")}`);
            }
        });
        return problems;
    }, [visibleSlabs]);

    // ---- slab row editing (index-based: order is the source of truth) ----
    const patchSlab = (index, patch) =>
        updateVisible((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    // Appends a band. The row that was open-ended becomes a closed band whose
    // upper limit you now type, and the new top band inherits it automatically.
    const addSlab = () => updateVisible((rows) => [
        ...rows,
        { _tmp: `tmp-${Date.now()}`, regime, age_category: ageCategory, income_to: null, rate: "" },
    ]);

    // relink() re-chains the survivors, so deleting a middle band closes the hole
    // rather than leaving a gap.
    const removeSlab = (index) => updateVisible((rows) => rows.filter((_, i) => i !== index));

    const patchConfig = (patch) => setConfigs((prev) => {
        const existing = prev.find((c) => c.regime === regime);
        if (existing) return prev.map((c) => (c.regime === regime ? { ...c, ...patch } : c));
        return [...prev, { ...emptyConfig(regime), ...patch }];
    });

    // ---- surcharge bands ----
    const surchargeBands = currentConfig.surcharge_bands || [];
    const patchBand = (index, patch) => patchConfig({
        surcharge_bands: surchargeBands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });
    const addBand = () => patchConfig({ surcharge_bands: [...surchargeBands, { from: "", rate: "" }] });
    const removeBand = (index) => patchConfig({ surcharge_bands: surchargeBands.filter((_, i) => i !== index) });

    const handleSave = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to change TDS configuration"); return; }
        if (gridProblems.length) { ToastMsg("error", "Fix the slab grid before saving"); return; }

        const payload = {
            financial_year: financialYear,
            tds_enabled: tdsEnabled,
            default_regime: defaultRegime,
            // Send only the grid on screen. The backend replaces that
            // (regime, age) grid wholesale, so other grids stay untouched.
            slabs: visibleSlabs.map((s) => ({
                regime, age_category: ageCategory,
                income_from: numOrZero(s.income_from),
                income_to: s.income_to === "" || s.income_to === null || s.income_to === undefined ? null : numOrZero(s.income_to),
                rate: numOrZero(s.rate),
            })),
            configs: [{
                regime,
                standard_deduction: numOrZero(currentConfig.standard_deduction),
                rebate_87a_income_limit: currentConfig.rebate_87a_income_limit === "" ? null : numOrZero(currentConfig.rebate_87a_income_limit),
                rebate_87a_max_amount: numOrZero(currentConfig.rebate_87a_max_amount),
                cess_rate: numOrZero(currentConfig.cess_rate),
                surcharge_bands: surchargeBands
                    .filter((b) => b.from !== "" && b.from !== null)
                    .map((b) => ({ from: numOrZero(b.from), rate: numOrZero(b.rate) })),
                surcharge_max_rate: currentConfig.surcharge_max_rate === "" ? null : numOrZero(currentConfig.surcharge_max_rate),
                marginal_relief_enabled: currentConfig.marginal_relief_enabled !== false,
                professional_tax_annual: numOrZero(currentConfig.professional_tax_annual),
            }],
        };

        const res = await update(PAYROLL_TDS_SLAB, payload, false, setSaving);
        if (res?.status === "success") {
            ToastMsg("success", "TDS configuration saved");
            load();
        } else {
            const problems = res?.data?.problems;
            ToastMsg("error", problems?.length ? problems[0] : (res?.message || "Could not save configuration"));
        }
    };

    // Direct-URL guard. The sidebar hides this screen without the permission,
    // but the route is reachable by typing the address.
    if (!viewPermission) {
        return (
            <ContainerCard scrollable>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center py-20 text-center">
                    <ShieldAlert className="h-12 w-12 text-rose-500 mb-4" />
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No access to TDS Configuration</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                        This screen sets the income tax rates used for the whole company, so it needs the
                        <strong> TDS Configuration </strong> permission. Ask an administrator to grant it under
                        User Permissions &rarr; Payroll.
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/payroll/payrollprocess")}
                        className="mt-6 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Back to Payroll
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300">Loading TDS configuration...</p>
            </div>
        );
    }

    const activeRegime = REGIMES.find((r) => r.key === regime);

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <div className="flex items-center gap-4 mb-4 p-3">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => navigate("/payroll/payrollprocess")}
                />
                <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">TDS Configuration</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Income tax slabs and constants per financial year — used by payroll to compute TDS
                    </p>
                </div>
            </div>

            {/* Rates-are-data notice */}
            <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3">
                <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 dark:text-amber-200">
                    <p className="font-semibold mb-1">Enter the rates your CA confirms — nothing is pre-filled.</p>
                    <p>
                        Slabs are stored against the financial year, so changing next year's rates never alters a payslip
                        already issued. Until slabs exist for a year, payroll deducts <strong>zero</strong> TDS rather than
                        guessing a rate.
                    </p>
                </div>
            </div>

            {/* Company-level switches */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 space-y-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Financial year</label>
                    <Select
                        value={financialYear}
                        onChange={setFinancialYear}
                        options={fyOptions}
                        className={CONTROL_CLASS}
                        size="large"
                    />
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex flex-col min-w-0">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Deduct TDS in payroll
                            </label>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                {tdsEnabled ? "Payroll will compute and deduct tax" : "Payroll runs as before, deducting no tax"}
                            </span>
                        </div>
                        <Toggle checked={tdsEnabled} onChange={() => setTdsEnabled((v) => !v)} disabled={!editPermission} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 space-y-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Default regime</label>
                    <Select
                        value={defaultRegime}
                        onChange={setDefaultRegime}
                        options={REGIME_OPTIONS}
                        className={CONTROL_CLASS}
                        size="large"
                    />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Applied to anyone who has not opted in.</p>
                </div>
            </div>

            {/* Regime tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                    {REGIMES.map((r) => (
                        <button
                            key={r.key}
                            type="button"
                            onClick={() => setRegime(r.key)}
                            className={cn(
                                "px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                                regime === r.key
                                    ? "bg-sidebar-primary text-white shadow-sm"
                                    : "border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                        >
                            {r.label}
                        </button>
                    ))}
                    {regime === "old" && (
                        <div className="ml-2 w-44">
                            <Select
                                value={ageCategory}
                                onChange={setAgeCategory}
                                options={AGE_CATEGORIES}
                                className={CONTROL_CLASS}
                                size="large"
                            />
                        </div>
                    )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    {activeRegime?.hint}
                    {regime === "old" && " Age changes the grid only under this regime."}
                </p>
            </div>

            {/* Grid problems */}
            {gridProblems.length > 0 && (
                <div className="mb-4 rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        <span className="text-sm font-semibold text-rose-800 dark:text-rose-300">
                            This grid cannot be saved yet
                        </span>
                    </div>
                    <ul className="list-disc list-inside text-sm text-rose-700 dark:text-rose-300 space-y-0.5">
                        {gridProblems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                </div>
            )}
            {serverValidation.length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Saved grids with problems</p>
                    <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-300 space-y-0.5">
                        {serverValidation.map((v, i) => <li key={i}>{v.regime}/{v.age_category}: {v.problem}</li>)}
                    </ul>
                </div>
            )}

            {/* Slab grid */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-4">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Marginal slab bands</h2>
                    <Button
                        size="sm" variant="brand" onClick={addSlab} disabled={!editPermission}
                        className="shadow-sm"
                    >
                        <Plus className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Add band</span>
                    </Button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <th className="px-4 py-3 font-medium">Income from (₹)</th>
                                <th className="px-4 py-3 font-medium">Income to (₹)</th>
                                <th className="px-4 py-3 font-medium">Rate (%)</th>
                                <th className="px-4 py-3 font-medium w-16 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {visibleSlabs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                                        No bands yet — add the lowest band starting at 0.
                                    </td>
                                </tr>
                            ) : visibleSlabs.map((slab, index) => {
                                const isLast = index === visibleSlabs.length - 1;
                                const from = slab.income_from;
                                return (
                                    <tr key={rowKeyOf(slab) ?? index} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/30">
                                        {/* Read-only: derived from the band below, so bands can never
                                            drift apart. Only the upper limit is typed. */}
                                        <td className="px-4 py-3">
                                            <div className="w-32 px-3 py-2 text-sm text-right rounded-md bg-gray-100 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                                {from === "" || from === null || from === undefined
                                                    ? <span className="text-gray-400 italic text-xs">set above</span>
                                                    : Number(from).toLocaleString("en-IN")}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {isLast ? (
                                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                                    and above — use <strong>Add band</strong> to close this off
                                                </span>
                                            ) : (
                                                <NumInput value={slab.income_to ?? ""} disabled={!editPermission}
                                                    onChange={(v) => patchSlab(index, { income_to: v })} />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <NumInput value={slab.rate} step="0.5" disabled={!editPermission}
                                                onChange={(v) => patchSlab(index, { rate: v })} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                variant="ghost" size="icon" disabled={!editPermission}
                                                onClick={() => removeSlab(index)}
                                                className="h-8 w-8 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                                                aria-label="Remove band"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                    Each rate applies only to the portion of income inside its band. <strong>Income from</strong> is filled
                    in for you — a band always starts where the one below it ends — so you only type the upper limit and the
                    rate. The highest band stays open-ended; press <strong>Add band</strong> to close it off and start a new
                    one above it.
                </p>
            </div>

            {/* Per-regime constants */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    Constants — {activeRegime?.label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Standard deduction (₹)" value={currentConfig.standard_deduction} disabled={!editPermission}
                        onChange={(v) => patchConfig({ standard_deduction: v })} />
                    <Field label="Section 87A — income limit (₹)" value={currentConfig.rebate_87a_income_limit} disabled={!editPermission}
                        onChange={(v) => patchConfig({ rebate_87a_income_limit: v })} />
                    <Field label="Section 87A — max rebate (₹)" value={currentConfig.rebate_87a_max_amount} disabled={!editPermission}
                        onChange={(v) => patchConfig({ rebate_87a_max_amount: v })} />
                    <Field label="Health & education cess (%)" value={currentConfig.cess_rate} step="0.1" disabled={!editPermission}
                        onChange={(v) => patchConfig({ cess_rate: v })} />
                    <Field label="Surcharge cap (%)" value={currentConfig.surcharge_max_rate} step="0.1" disabled={!editPermission}
                        onChange={(v) => patchConfig({ surcharge_max_rate: v })} />
                    {regime === "old" && (
                        <Field label="Professional tax, annual (₹)" value={currentConfig.professional_tax_annual} disabled={!editPermission}
                            onChange={(v) => patchConfig({ professional_tax_annual: v })} />
                    )}
                </div>

                {/* Marginal relief */}
                <div className="mt-4 flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex flex-col min-w-0 pr-3">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Apply marginal relief</label>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Crossing a surcharge threshold can never cost more tax than the income gained
                        </span>
                    </div>
                    <Toggle
                        checked={currentConfig.marginal_relief_enabled !== false}
                        onChange={() => patchConfig({ marginal_relief_enabled: !(currentConfig.marginal_relief_enabled !== false) })}
                        disabled={!editPermission}
                    />
                </div>

                {/* Surcharge bands */}
                <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Surcharge bands</h3>
                        <Button
                            variant="outline" size="sm" onClick={addBand} disabled={!editPermission}
                            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <Plus className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Add band</span>
                        </Button>
                    </div>
                    {surchargeBands.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            None configured. Add a band for each threshold that carries surcharge — anyone above ₹50L
                            needs this, and it is the fiddliest part of the calculation.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {surchargeBands.map((band, index) => (
                                <div key={index}
                                    className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-20">Above (₹)</span>
                                    <NumInput value={band.from} disabled={!editPermission}
                                        onChange={(v) => patchBand(index, { from: v })} />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">rate (%)</span>
                                    <NumInput value={band.rate} step="0.5" disabled={!editPermission}
                                        onChange={(v) => patchBand(index, { rate: v })} />
                                    <Button
                                        variant="ghost" size="icon" disabled={!editPermission}
                                        onClick={() => removeBand(index)}
                                        className="h-8 w-8 ml-auto text-gray-400 hover:text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-900/20"
                                        aria-label="Remove surcharge band"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                <p className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5 pl-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                    Saving replaces only the grid shown above ({regime}{regime === "old" ? ` / ${ageCategory}` : ""}).
                </p>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline" onClick={() => navigate("/payroll/payrollprocess")} disabled={saving}
                        className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>
                    <Button
                        variant="brand"
                        onClick={handleSave}
                        disabled={saving || !editPermission || gridProblems.length > 0}
                        className={cn(
                            "transition-colors duration-200 flex items-center gap-2",
                            (saving || !editPermission || gridProblems.length > 0) && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        {saving
                            ? <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            : <Save className="w-4 h-4" />}
                        Save configuration
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

const rowKeyOf = (slab) => slab.id ?? slab._tmp;
const numOrZero = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));

/** Toggle switch — the same control the configuration screens use. */
const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        aria-pressed={!!checked}
        className={cn(
            "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200",
            checked ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
    >
        <span
            className={cn(
                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                checked ? "translate-x-4" : "translate-x-1"
            )}
        />
    </button>
);

const NumInput = ({ value, onChange, step = "1", disabled }) => (
    <Input
        type="number" step={step} value={value ?? ""} disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className={cn(INPUT_CLASS, "w-32 text-right")}
    />
);

const Field = ({ label, value, onChange, step = "1", disabled }) => (
    <div className="space-y-2">
        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <Input
            type="number" step={step} value={value ?? ""} disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            className={cn(INPUT_CLASS, "w-full")}
        />
    </div>
);

export default TDSConfiguration;
